/**
 * Battle session state — shared between lobby and battle screens.
 * Holds the engine, sync manager, replay recorder, and team data
 * so they can survive screen transitions.
 */

import { BattleEngine, type BattleEngine as Engine, generateSeed } from "../engine/battle";
import type { BattleConfig, BattleState, PlayerId, PlayerAction, PokemonSet, TrainerProfile, Team } from "../engine/types";
import { ReplayRecorder } from "../engine/replay";
import type { SyncManager } from "../p2p/sync";

export type BattleSession = {
  engine: Engine;
  sync: SyncManager;
  recorder: ReplayRecorder;
  mySide: PlayerId;
  isHost: boolean;
  config: BattleConfig;
  myProfile: TrainerProfile;
  opponentProfile: TrainerProfile;
  myTeam: PokemonSet[];
  opponentTeam: PokemonSet[];
  /** Resolve a pending turn — called when both actions are ready. */
  resolveTurn: () => BattleState | null;
  /** Get the last actions submitted for the current turn (for sound effects). */
  getLastActions: () => { p1: PlayerAction["action"]; p2: PlayerAction["action"] } | null;
  /** Get timer config. */
  turnTimerSeconds: number;
};

let currentSession: BattleSession | null = null;

export function getBattleSession(): BattleSession | null {
  return currentSession;
}

export function setBattleSession(session: BattleSession | null): void {
  currentSession = session;
}

export function clearBattleSession(): void {
  currentSession = null;
}

/** Create a battle session from two teams. Used by both host and guest. */
export function createBattleSession(
  seed: number,
  config: BattleConfig,
  sync: SyncManager,
  myProfile: TrainerProfile,
  opponentProfile: TrainerProfile,
  myTeam: Team,
  opponentTeam: Team,
  isHost: boolean,
): BattleSession {
  const engine = BattleEngine.create(config, seed, myTeam, opponentTeam);
  const recorder = new ReplayRecorder(config, myProfile, opponentProfile, myTeam.members, opponentTeam.members);
  const mySide: PlayerId = isHost ? "p1" : "p2";

  recorder.setInitialState(engine.getState());
  const session: BattleSession = {
    engine,
    sync,
    recorder,
    mySide,
    isHost,
    config,
    myProfile,
    opponentProfile,
    myTeam: myTeam.members,
    opponentTeam: opponentTeam.members,
    turnTimerSeconds: config.turnTimerSeconds,
    resolveTurn: () => {
      if (!sync.isTurnReady(sync.getState().turn)) return null;
      const turn = sync.getState().turn;
      const myAction = sync.getState().pendingAction!;
      const peerAction = sync.getPeerAction(turn)!;
      const playerA: PlayerAction = { player: "p1", action: isHost ? myAction : peerAction };
      const playerB: PlayerAction = { player: "p2", action: isHost ? peerAction : myAction };
      engine.submitActions(playerA, playerB);
      const state = engine.getState();
      recorder.recordTurn(turn, [playerA, playerB], state);
      sync.advanceTurn();
      return state;
    },
    getLastActions: () => {
      if (!sync.isTurnReady(sync.getState().turn)) return null;
      const turn = sync.getState().turn;
      const myAction = sync.getState().pendingAction!;
      const peerAction = sync.getPeerAction(turn)!;
      return {
        p1: isHost ? myAction : peerAction,
        p2: isHost ? peerAction : myAction,
      };
    },
  };

  currentSession = session;
  return session;
}

/** Generate a battle seed (calls through to engine helper). */
export { generateSeed };

/** Reset an existing battle session for a rematch. Keeps the same teams and profiles but creates a new engine and recorder. */
export function resetBattleSession(session: BattleSession, seed: number, config: BattleConfig): BattleSession {
  const { sync, myProfile, opponentProfile, myTeam, opponentTeam, isHost } = session;
  const engine = BattleEngine.create(config, seed, isHost ? { id: "host", name: myProfile.name, format: "gen5", members: myTeam, createdAt: "", updatedAt: "" } : { id: "guest", name: myProfile.name, format: "gen5", members: myTeam, createdAt: "", updatedAt: "" }, isHost ? { id: "guest", name: opponentProfile.name, format: "gen5", members: opponentTeam, createdAt: "", updatedAt: "" } : { id: "host", name: opponentProfile.name, format: "gen5", members: opponentTeam, createdAt: "", updatedAt: "" });
  const recorder = new ReplayRecorder(config, myProfile, opponentProfile, myTeam, opponentTeam);

  recorder.setInitialState(engine.getState());
  const newSession: BattleSession = {
    ...session,
    engine,
    recorder,
    config,
    turnTimerSeconds: config.turnTimerSeconds,
    resolveTurn: () => {
      if (!sync.isTurnReady(sync.getState().turn)) return null;
      const turn = sync.getState().turn;
      const myAction = sync.getState().pendingAction!;
      const peerAction = sync.getPeerAction(turn)!;
      const playerA: PlayerAction = { player: "p1", action: isHost ? myAction : peerAction };
      const playerB: PlayerAction = { player: "p2", action: isHost ? peerAction : myAction };
      engine.submitActions(playerA, playerB);
      const state = engine.getState();
      recorder.recordTurn(turn, [playerA, playerB], state);
      sync.advanceTurn();
      return state;
    },
    getLastActions: () => {
      if (!sync.isTurnReady(sync.getState().turn)) return null;
      const turn = sync.getState().turn;
      const myAction = sync.getState().pendingAction!;
      const peerAction = sync.getPeerAction(turn)!;
      return {
        p1: isHost ? myAction : peerAction,
        p2: isHost ? peerAction : myAction,
      };
    },
  };

  currentSession = newSession;
  return newSession;
}
